import axios from 'axios';
import {
  fetchSubadminsStart,
  fetchSubadminsSuccess,
  fetchSubadminsFailure,
  fetchClientAdminsStart,
  fetchClientAdminsSuccess,
  fetchClientAdminsFailure,
  fetchDevicesStart,
  fetchDevicesSuccess,
  fetchDevicesFailure,
  upsertDevice,
  createDeviceSuccess,
  createDeviceFailure,
  editDeviceStart,
  editDeviceSuccess,
  editDeviceFailure,
  toggleDeviceStatusStart,
  toggleDeviceStatusSuccess,
  toggleDeviceStatusFailure,
  fetchsupervisorsuccess,
  fetchsupervisorfailure,
  fetchsupervisorstart,
} from './deviceSlice';
import { API_BASE_URL } from '../config/config';


export const fetchSupervisorsForClientAdmin = () => async (dispatch) => {
  dispatch(fetchsupervisorstart());
  try {
    const response = await axios.get(`${API_BASE_URL}api/devices/supervisors/clientadmin`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
    });
    dispatch(fetchsupervisorsuccess(response.data.supervisors));
  } catch (error) {
    dispatch(fetchsupervisorfailure(error.response?.data?.message || 'Failed to fetch supervisors'));
  }
};
// Fetch Subadmins
export const fetchSubadmins = () => async (dispatch, getState) => {
  dispatch(fetchSubadminsStart());
  try {
    const { token } = getState().user; // Get JWT token from user state
    const response = await axios.get(`${API_BASE_URL}api/users/subadmins`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    dispatch(fetchSubadminsSuccess(response.data.subadmins));
  } catch (error) {
    dispatch(fetchSubadminsFailure(error.response?.data?.message || 'Failed to fetch Subadmins'));
  }
};

// Fetch Client Admins for a specific Subadmin (Admin only)
export const fetchClientAdminsForSubadmin = (subadminId) => async (dispatch, getState) => {
  dispatch(fetchClientAdminsStart());
  try {
    const { token } = getState().user;
    const response = await axios.get(`${API_BASE_URL}api/users/subadmins/${subadminId}/clientadmins`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    dispatch(fetchClientAdminsSuccess(response.data.clientAdmins));
  } catch (error) {
    dispatch(fetchClientAdminsFailure(error.response?.data?.message || 'Failed to fetch Client Admins'));
  }
};

// Fetch Client Admins for the logged-in Subadmin (Subadmin only)
export const fetchClientAdminsForLoggedInSubadmin = () => async (dispatch, getState) => {
  dispatch(fetchClientAdminsStart());
  try {
    const { token } = getState().user;
    const response = await axios.get(`${API_BASE_URL}api/users/clientadmins`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    dispatch(fetchClientAdminsSuccess(response.data.clientAdmins));
  } catch (error) {
    dispatch(fetchClientAdminsFailure(error.response?.data?.message || 'Failed to fetch Client Admins for logged-in Subadmin'));
  }
};

// Build a clean axios query-params object from a pagination/search params bag.
// Only defined, non-empty values are forwarded so the backend applies its own
// defaults (page=1, limit=20) when nothing is supplied.
const buildDeviceParams = (params = {}) => {
  const out = {};
  if (params.page) out.page = params.page;
  if (params.limit) out.limit = params.limit;
  if (params.search && String(params.search).trim()) out.search = String(params.search).trim();
  if (params.client) out.client = params.client;
  if (params.location) out.location = params.location;
  if (params.deviceType) out.deviceType = params.deviceType;
  return out;
};

// Fetch devices for Admin
export const fetchDevicesForAdmin = (params = {}) => async (dispatch, getState) => {
  dispatch(fetchDevicesStart());
  try {
    const { token } = getState().user;
    const response = await axios.get(`${API_BASE_URL}api/devices/admin`, {
      headers: { Authorization: `Bearer ${token}` },
      params: buildDeviceParams(params),
    });
    dispatch(fetchDevicesSuccess(response.data));
  } catch (error) {
    dispatch(fetchDevicesFailure(error.response?.data?.message || 'Failed to fetch devices for Admin'));
  }
};

// Fetch devices for Subadmin
export const fetchDevicesForSubadmin = (params = {}) => async (dispatch, getState) => {
  dispatch(fetchDevicesStart());
  try {
    const { token } = getState().user;
    const response = await axios.get(`${API_BASE_URL}api/devices/subadmin`, {
      headers: { Authorization: `Bearer ${token}` },
      params: buildDeviceParams(params),
    });
    dispatch(fetchDevicesSuccess(response.data));
  } catch (error) {
    dispatch(fetchDevicesFailure(error.response?.data?.message || 'Failed to fetch devices for Subadmin'));
  }
};

export const fetchDevicesForClientAdmin = (params = {}) => async (dispatch, getState) => {
  dispatch(fetchDevicesStart());
  try {
    const { token } = getState().user;
    const response = await axios.get(`${API_BASE_URL}api/devices/clientadmin`, {
      headers: { Authorization: `Bearer ${token}` },
      params: buildDeviceParams(params),
    });
    dispatch(fetchDevicesSuccess(response.data));  // Dispatch the success action with the devices
  } catch (error) {
    dispatch(fetchDevicesFailure(error.response?.data?.message || 'Failed to fetch devices for ClientAdmin'));  // Dispatch the failure action with the error message
  }
};

export const fetchDevicesForSupervisor = (params = {}) => async (dispatch, getState) => {
  dispatch(fetchDevicesStart());
  try {
    const { token } = getState().user;
    const response = await axios.get(`${API_BASE_URL}api/devices/supervisor`, {
      headers: { Authorization: `Bearer ${token}` },
      params: buildDeviceParams(params),
    });
    dispatch(fetchDevicesSuccess(response.data));
  } catch (error) {
    dispatch(fetchDevicesFailure(error.response?.data?.message || 'Failed to fetch devices for Supervisor'));
  }
}

// Fetch a single device by its code. Lets the control panel resolve a device on
// a direct link / hard refresh without depending on the paginated list page.
export const fetchDeviceByCode = (deviceCode) => async (dispatch, getState) => {
  try {
    const { token } = getState().user;
    const response = await axios.get(`${API_BASE_URL}api/devices/by-code/${deviceCode}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const device = response.data?.device || response.data;
    dispatch(upsertDevice(device));
    return device;
  } catch (error) {
    dispatch(fetchDevicesFailure(error.response?.data?.message || 'Failed to fetch device'));
    return null;
  }
};

// Create a new device
export const createDevice = (newDeviceData) => async (dispatch, getState) => {
  try {
    const { token } = getState().user;
    const response = await axios.post(`${API_BASE_URL}api/devices/create`, newDeviceData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    dispatch(createDeviceSuccess(response.data.device));
    return response; // Return the response in case you want to handle it in the component
  } catch (error) {
    const errorMessage = error.response?.data?.message || 'Failed to create device';
    dispatch(createDeviceFailure(errorMessage)); // Dispatch error message
    throw new Error(errorMessage); // Throw an error so the component can catch it
  }
};
export const editDevice = (deviceId, updatedDeviceData) => async (dispatch, getState) => {
  dispatch(editDeviceStart());
  try {
    const { token } = getState().user;
    const response = await axios.patch(`${API_BASE_URL}api/devices/${deviceId}/edit`, updatedDeviceData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    dispatch(editDeviceSuccess(response.data.device));
  } catch (error) {
    dispatch(editDeviceFailure(error.response?.data?.message || 'Failed to update device'));
    throw new Error(error.response?.data?.message || 'Failed to update device');
  }
};

// Toggle device status (activate/inactivate)
export const toggleDeviceStatus = (deviceId, status) => async (dispatch, getState) => {
  dispatch(toggleDeviceStatusStart());
  try {
    const { token } = getState().user;
    const response = await axios.post(`${API_BASE_URL}api/devices/${deviceId}/toggle-status`, { status }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    dispatch(toggleDeviceStatusSuccess(response.data.device));
  } catch (error) {
    dispatch(toggleDeviceStatusFailure(error.response?.data?.message || 'Failed to toggle device status'));
  }
};